/**
 * A Writing script: copying it out, counting it, and when it goes.
 *
 * Shared by the marker's screen and the candidate's, so the two Copy buttons
 * behave alike and both say the same date.
 */

/** How long a script is kept once its result has reached the candidate. */
export const SCRIPT_RETENTION_MS = 48 * 60 * 60 * 1000;

export const wordCount = (text: string): number => {
  const t = text.trim();
  return t ? t.split(/\s+/).length : 0;
};

/**
 * When the script will be cleared, or null if that is not yet decided.
 *
 * The two days run from the moment the result reaches the candidate — the
 * later of its marking and its release — because that is the first moment
 * they can read it. Until both have happened there is no date at all: an
 * unmarked script is still being waited on, and a marked one nobody has
 * released has not yet been seen by the person who wrote it.
 */
export function clearsAt(markedAt: string | null, releasedAt: string | null): Date | null {
  if (!markedAt || !releasedAt) return null;
  const from = Math.max(new Date(markedAt).getTime(), new Date(releasedAt).getTime());
  return Number.isFinite(from) ? new Date(from + SCRIPT_RETENTION_MS) : null;
}

/**
 * Puts a script on the clipboard with its paragraph breaks, so it pastes into
 * Word as it was written. Returns whether it got there.
 *
 * Clipboard access can be refused — an insecure origin, a browser that wants a
 * user gesture it did not see. Then the script on screen is selected instead,
 * which leaves the reader one keystroke from the same result rather than a
 * button that did nothing and said nothing.
 */
export async function copyScript(text: string, shownIn: HTMLElement | null): Promise<boolean> {
  if (!text.trim()) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    if (shownIn) {
      const range = document.createRange();
      range.selectNodeContents(shownIn);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    return false;
  }
}
